/**
 * Frontend utilities for handling Convex errors.
 *
 * Convex client/runtime messages can include function paths, deployment details,
 * and transport state. Keep those out of customer-facing UI.
 */

import { notifyAuthRequired } from "@/lib/auth-required";
import { reportHandledWebClientError } from "@/lib/client-error-reporting";
import { sanitizeUserFacingErrorMessage } from "@shared/utils/userFacingErrors";

const CONVEX_UNCAUGHT_PREFIX = /^Uncaught\s+ConvexError:\s*/i;
const UNCAUGHT_ERROR_PREFIX = /^Uncaught\s+Error:\s*/i;
const CONVEX_CLASS_PREFIX = /^ConvexError:\s*/i;
const GENERIC_ERROR_PREFIX = /^Error:\s*/i;
const CONVEX_DETAILS_SUFFIX = /\s*\|\s*\{[\s\S]*\}\s*$/;
const CONVEX_ERROR_CODE_PREFIX = /^[A-Z_]+:\s*/;
const CONVEX_FUNCTION_TAG = /\[CONVEX\s+[A-Z]\([^)]*\)\]/i;
const CONVEX_REQUEST_ID = /\[Request ID:[^\]]+\]/i;
const STACK_LINE = /^\s*at\s+/;

const INTERNAL_CONVEX_PATTERNS = [
  /\[CONVEX\b/i,
  /\bConvexError\b/i,
  /\bConvex\b.*\b(action|query|mutation|deployment|function)\b/i,
  /\b(actions|queries|mutations)\/[A-Za-z0-9_/-]+/i,
  /\bCalled by client\b/i,
  /\bConnection lost\b/i,
  /\bwhile action was in flight\b/i,
  /\bWebSocket\b/i,
  /\bfetch failed\b/i,
  /\bnetwork request failed\b/i,
];

const CONVEX_TRANSPORT_PATTERNS = [
  /\bConnection lost\b/i,
  /\bwhile action was in flight\b/i,
  /\bwhile request was in flight\b/i,
  /\bWebSocket\b/i,
  /\bfetch failed\b/i,
  /\bfailed to fetch\b/i,
  /\bnetwork error\b/i,
  /\bnetworkerror\b/i,
  /\bnetwork request failed\b/i,
  /\boffline\b/i,
];

const CONVEX_ORIGIN_PATTERNS = [
  /\[CONVEX\b/i,
  /\bConvexError\b/i,
  /\bUncaught\s+ConvexError\b/i,
  /\bConvex\b.*\b(action|query|mutation|deployment|function|http action)\b/i,
  /\b(actions|queries|mutations)\/[A-Za-z0-9_/-]+/i,
  /\bCalled by client\b/i,
  /\[Request ID:[^\]]+\]/i,
];

const DEFAULT_CONVEX_ERROR_MESSAGE = 'Something went wrong. Please try again.';

function extractErrorMessage(error: unknown): string | null {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null) {
    const possibleMessage = (error as { message?: unknown }).message;
    if (typeof possibleMessage === 'string') {
      return possibleMessage;
    }

    const possibleData = (error as { data?: { message?: unknown; error?: unknown } }).data;
    if (possibleData && typeof possibleData.message === 'string') {
      return possibleData.message;
    }
    if (possibleData && typeof possibleData.error === 'string') {
      return possibleData.error;
    }
    if (
      possibleData &&
      typeof possibleData.error === 'object' &&
      possibleData.error !== null &&
      typeof (possibleData.error as { message?: unknown }).message === 'string'
    ) {
      return (possibleData.error as { message: string }).message;
    }

    const possibleError = (error as { error?: unknown }).error;
    if (typeof possibleError === 'string') {
      return possibleError;
    }
  }

  return null;
}

function cleanConvexMessage(message: string): string {
  const withoutJsonDetails = message.trim().replace(CONVEX_DETAILS_SUFFIX, '');
  const lines = withoutJsonDetails
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !STACK_LINE.test(line));

  const uncaughtLine =
    lines.find((line) => UNCAUGHT_ERROR_PREFIX.test(line) || CONVEX_UNCAUGHT_PREFIX.test(line)) ||
    lines.find((line) => GENERIC_ERROR_PREFIX.test(line) || CONVEX_CLASS_PREFIX.test(line)) ||
    lines.find((line) => !CONVEX_FUNCTION_TAG.test(line) && !/^Server Error$/i.test(line)) ||
    lines[0] ||
    withoutJsonDetails;

  return uncaughtLine
    .trim()
    .replace(CONVEX_FUNCTION_TAG, '')
    .replace(CONVEX_REQUEST_ID, '')
    .replace(CONVEX_UNCAUGHT_PREFIX, '')
    .replace(UNCAUGHT_ERROR_PREFIX, '')
    .replace(CONVEX_CLASS_PREFIX, '')
    .replace(GENERIC_ERROR_PREFIX, '')
    .replace(/^Server Error\s*/i, '')
    .replace(/\s*Called by client[\s\S]*$/i, '')
    .replace(CONVEX_ERROR_CODE_PREFIX, '')
    .trim();
}

function containsInternalConvexDetails(message: string): boolean {
  return INTERNAL_CONVEX_PATTERNS.some((pattern) => pattern.test(message));
}

function isConvexTransportError(message: string): boolean {
  return CONVEX_TRANSPORT_PATTERNS.some((pattern) => pattern.test(message));
}

function isGenericUnsafeMessage(message: string): boolean {
  const normalized = message.toLowerCase().replace(/[.!]+$/g, '').trim();
  return (
    normalized.length === 0 ||
    normalized === 'server error' ||
    normalized === 'internal server error' ||
    normalized === 'something broke' ||
    normalized === 'called by client' ||
    normalized === 'an unexpected error occurred'
  );
}

function isConvexOriginError(error: unknown, message: string): boolean {
  if (CONVEX_ORIGIN_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }

  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const name = (error as { name?: unknown }).name;
  if (typeof name === 'string' && /\bconvex\b/i.test(name)) {
    return true;
  }

  const stack = (error as { stack?: unknown }).stack;
  if (typeof stack === 'string' && /\bconvex\b/i.test(stack)) {
    return true;
  }

  const data = (error as { data?: unknown }).data;
  if (typeof data === 'object' && data !== null) {
    const dataName = (data as { name?: unknown }).name;
    if (typeof dataName === 'string' && /\bconvex\b/i.test(dataName)) {
      return true;
    }

    const dataCode = (data as { code?: unknown }).code;
    if (typeof dataCode === 'string' && /^CONVEX_/i.test(dataCode)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if an error is authentication-related
 * @param error - Error to check
 * @returns true if error is authentication-related
 */
export function isAuthenticationError(error: unknown): boolean {
  const rawMessage = extractErrorMessage(error);
  if (rawMessage) {
    if (isConvexTransportError(rawMessage)) {
      return false;
    }

    const message = cleanConvexMessage(rawMessage).toLowerCase();
    return (
      message.includes('not authenticated') ||
      message.includes('missing or invalid token') ||
      message.includes('invalid or expired token') ||
      message.includes('session expired') ||
      message.includes('authentication required') ||
      (message.includes('access denied') && message.includes('not authenticated'))
    );
  }

  return false;
}

/**
 * Check if an error is authorization-related
 * @param error - Error to check
 * @returns true if error is authorization-related
 */
export function isAuthorizationError(error: unknown): boolean {
  const rawMessage = extractErrorMessage(error);
  if (rawMessage) {
    const message = cleanConvexMessage(rawMessage).toLowerCase();
    return (
      message.includes('access denied') ||
      message.includes('forbidden') ||
      message.includes('not authorized') ||
      message.includes('insufficient permissions') ||
      message.includes('unauthorized access') ||
      (message.includes('only') && (message.includes('can access') || message.includes('can modify')))
    );
  }

  return false;
}

/**
 * Get user-friendly error message from Convex error
 * @param error - Error from Convex
 * @param fallback - Safe fallback to show when the error contains internal details
 * @returns User-friendly error message
 */
export function getConvexErrorMessage(error: unknown, fallback = DEFAULT_CONVEX_ERROR_MESSAGE): string {
  const rawMessage = extractErrorMessage(error);
  const isConvexOrigin = rawMessage ? isConvexOriginError(error, rawMessage) : false;

  if (isAuthenticationError(error)) {
    notifyAuthRequired();
    const safeMessage = 'You need to be logged in to perform this action. Please log in and try again.';
    reportHandledWebClientError({
      type: isConvexOrigin ? "web_convex_auth_error" : "web_auth_error",
      safeMessage,
      error,
      context: {
        fallback,
      },
    });
    return safeMessage;
  }

  if (isAuthorizationError(error)) {
    const safeMessage = 'You do not have permission to perform this action.';
    reportHandledWebClientError({
      type: isConvexOrigin ? "web_convex_authorization_error" : "web_authorization_error",
      safeMessage,
      error,
      context: {
        fallback,
      },
    });
    return safeMessage;
  }

  if (!rawMessage) {
    return fallback;
  }

  if (isConvexTransportError(rawMessage)) {
    reportHandledWebClientError({
      type: isConvexOrigin ? "web_convex_transport_error" : "web_network_error",
      safeMessage: fallback,
      error,
      context: {
        fallback,
      },
    });
    return fallback;
  }

  const cleanedMessage = cleanConvexMessage(rawMessage);
  if (
    isGenericUnsafeMessage(cleanedMessage) ||
    containsInternalConvexDetails(cleanedMessage)
  ) {
    reportHandledWebClientError({
      type: isConvexOrigin ? "web_convex_internal_error" : "web_client_error",
      safeMessage: fallback,
      error,
      context: {
        fallback,
      },
    });
    return fallback;
  }

  const safeMessage = sanitizeUserFacingErrorMessage(cleanedMessage || rawMessage, fallback);
  reportHandledWebClientError({
    type: isConvexOrigin ? "web_convex_handled_error" : "web_handled_error",
    safeMessage,
    error,
    context: {
      fallback,
    },
  });
  return safeMessage;
}
